import {
  getPushServerConfig,
  registerPushDevice,
  unregisterPushDevice,
} from "../api/pushApi";
import {
  detectPushPlatform,
  getPushCapability,
  getPushInstallationId,
} from "./pushInstallation";
import { getAppServiceWorkerRegistration } from "./appServiceWorker";

const FOREGROUND_EVENT_NAME = "chekhabare:push-foreground";
let messageLifecycleInstalled = false;
const permissionPromptAttemptedInSession = {
  app: false,
  browser: false,
};
let cachedServerConfig = null;
let serverConfigPromise = null;


const base64UrlToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
};

const loadServerConfig = async ({ force = false } = {}) => {
  if (!force && cachedServerConfig) return cachedServerConfig;
  if (!force && serverConfigPromise) return serverConfigPromise;

  serverConfigPromise = getPushServerConfig()
    .then((response) => {
      const data = response?.data || {};
      cachedServerConfig = {
        enabled: data.enabled === true,
        configured: data.configured === true,
        vapidPublicKey: String(data.vapid_public_key || "").trim(),
      };
      return cachedServerConfig;
    })
    .finally(() => {
      serverConfigPromise = null;
    });

  return serverConfigPromise;
};

const getRegistration = async () => {
  const existing = getAppServiceWorkerRegistration();
  if (existing) return existing;

  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
};

const installForegroundMessageLifecycle = () => {
  if (
    messageLifecycleInstalled ||
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return;
  }

  messageLifecycleInstalled = true;
  navigator.serviceWorker.addEventListener("message", (event) => {
    if (event.data?.type !== "CHEKHABARE_PUSH_RECEIVED") return;
    window.dispatchEvent(
      new CustomEvent(FOREGROUND_EVENT_NAME, {
        detail: event.data.payload || {},
      })
    );
  });
};

const syncSubscriptionToBackend = async (subscription) => {
  if (!subscription) return null;
  return registerPushDevice({
    installationId: getPushInstallationId(),
    subscription,
    platform: detectPushPlatform(),
  });
};

export const getPushRuntimeStatus = async () => {
  const capability = getPushCapability();
  let server = { enabled: false, configured: false, vapidPublicKey: "" };
  try {
    server = await loadServerConfig();
  } catch {
    // Keep status usable when API is temporarily unavailable.
  }

  let subscribed = false;
  let endpoint = null;

  if (capability.supported) {
    const registration = await getRegistration();
    const subscription = await registration?.pushManager?.getSubscription?.();
    subscribed = Boolean(subscription);
    endpoint = subscription?.endpoint || null;
  }

  return {
    enabled: server.enabled,
    configured: server.configured,
    browserSupported: capability.supported,
    permission: capability.permission,
    subscribed,
    endpoint,
  };
};

/** Must only be called from a user gesture. */
export const enablePushNotifications = async ({ forceConfigRefresh = true } = {}) => {
  const server = await loadServerConfig({ force: forceConfigRefresh });
  if (!server.enabled) throw new Error("Push is disabled");
  if (!server.configured || !server.vapidPublicKey) {
    throw new Error("Web Push is not configured");
  }

  const capability = getPushCapability();
  if (!capability.supported) {
    throw new Error("Push notifications are not supported in this browser");
  }

  let permission = Notification.permission;
  if (permission === "default") permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Notification permission was not granted");
  }

  const registration = await getRegistration();
  if (!registration) throw new Error("App service worker is not ready");

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(server.vapidPublicKey),
    });
  }

  await syncSubscriptionToBackend(subscription);
  installForegroundMessageLifecycle();
  return {
    permission,
    subscribed: true,
    endpoint: subscription.endpoint,
    installationId: getPushInstallationId(),
    platform: detectPushPlatform(),
  };
};

/** Re-syncs an existing subscription without opening the permission prompt. */
export const syncExistingPushRegistration = async () => {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  ) {
    return null;
  }

  const server = await loadServerConfig();
  if (!server.enabled || !server.configured) return null;

  const registration = await getRegistration();
  if (!registration) return null;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return null;

  await syncSubscriptionToBackend(subscription);
  installForegroundMessageLifecycle();
  return {
    subscribed: true,
    endpoint: subscription.endpoint,
    installationId: getPushInstallationId(),
    platform: detectPushPlatform(),
  };
};

export const disablePushNotifications = async () => {
  const installationId = getPushInstallationId();
  const platform = detectPushPlatform();
  const registration = await getRegistration();
  const subscription = await registration?.pushManager?.getSubscription?.();

  let unsubscribeError = null;
  if (subscription) {
    try {
      await subscription.unsubscribe();
    } catch (error) {
      unsubscribeError = error;
    }
  }

  await unregisterPushDevice({ installationId, platform });
  if (unsubscribeError) throw unsubscribeError;
  return true;
};

export const subscribeToForegroundPush = (listener) => {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  installForegroundMessageLifecycle();
  const handler = (event) => listener(event.detail);
  window.addEventListener(FOREGROUND_EVENT_NAME, handler);
  return () => window.removeEventListener(FOREGROUND_EVENT_NAME, handler);
};


/**
 * Arms the notification permission request once for the current app launch.
 *
 * If permission is still "default", every fresh app/page launch gets a new
 * chance to ask on the first qualifying user gesture anywhere in the UI. We do
 * not persist an "already asked" marker in localStorage anymore. Therefore a
 * user who dismisses the browser prompt (permission remains "default") will be
 * asked again the next time the app is opened.
 *
 * Browsers, especially iOS/iPadOS PWAs, require requestPermission() to execute
 * directly inside a trusted user activation. pointerdown/touchstart/mousedown/
 * click/keydown are used as global gesture sources so the request happens at
 * the user's first real interaction after launch.
 *
 * If the browser permission is "granted", no prompt is shown and the existing
 * subscription is synced normally. If it is "denied", browsers do not allow
 * sites to reopen the system prompt; the user must change that permission in
 * browser/OS settings.
 */
export const armOneTimePushPermissionRequest = async () => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    typeof Notification === "undefined"
  ) {
    return false;
  }

  const isInstalledAppContext =
    window.navigator?.standalone === true ||
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.matchMedia?.("(display-mode: fullscreen)")?.matches === true ||
    document.referrer.startsWith("android-app://");

  const promptContext = isInstalledAppContext ? "app" : "browser";

  const capability = getPushCapability();
  if (!capability.supported || Notification.permission !== "default") {
    return false;
  }

  // Guard only for this runtime/session. There is deliberately no persistent
  // localStorage marker: a dismissed prompt should be eligible again on the
  // next fresh app launch while permission is still "default".
  if (permissionPromptAttemptedInSession[promptContext]) return false;

  const isIosDevice = /iPad|iPhone|iPod/.test(window.navigator?.userAgent || "") ||
    (window.navigator?.platform === "MacIntel" && window.navigator?.maxTouchPoints > 1);
  const isIosInstalledPwa =
    isIosDevice &&
    (window.navigator?.standalone === true ||
      window.matchMedia?.("(display-mode: standalone)")?.matches === true);

  let fired = false;

  // iOS/iPadOS standalone Web Apps are stricter than other targets about
  // notification permission. In particular, asking on pointerdown/touchstart
  // can consume the launch attempt without showing the native prompt. For the
  // installed iOS PWA, wait for a real click/tap activation on an interactive
  // control instead. Other platforms keep the v155 global-gesture behavior.
  const gestureEvents = isIosInstalledPwa
    ? ["click"]
    : ["pointerdown", "touchstart", "mousedown", "click", "keydown"];

  const cleanup = () => {
    gestureEvents.forEach((eventName) => {
      document.removeEventListener(eventName, handleFirstGlobalInteraction, true);
    });
  };

  const attach = () => {
    gestureEvents.forEach((eventName) => {
      document.addEventListener(eventName, handleFirstGlobalInteraction, true);
    });
  };

  const handleFirstGlobalInteraction = async (event) => {
    if (fired || Notification.permission !== "default") return;
    if (event?.isTrusted === false) return;

    if (isIosInstalledPwa) {
      // Keep the iOS request tied to an explicit app control. A tap on empty
      // layout/background space should not consume the permission opportunity.
      const target = event?.target;
      const interactiveTarget = target?.closest?.(
        'button, a, [role="button"], [role="link"], input, select, textarea, [data-push-permission-trigger="true"]'
      );
      if (!interactiveTarget) return;
    }

    // Ignore modifier-only key presses; normal keyboard navigation/actions are
    // valid triggers and remain supported on desktop.
    if (
      event?.type === "keydown" &&
      ["Shift", "Control", "Alt", "Meta", "CapsLock", "NumLock", "ScrollLock"].includes(event.key)
    ) {
      return;
    }

    fired = true;
    cleanup();

    let permission;
    try {
      // CRITICAL: invoke the browser permission API synchronously from the
      // native gesture callback, before any await, navigation, or network work.
      const request = Notification.requestPermission();
      permission = await request;
    } catch (error) {
      // If the browser rejects this particular gesture, keep the lifecycle
      // armed so a later qualifying interaction can still succeed.
      fired = false;
      attach();
      console.error("CheKhabare push permission request failed:", error);
      return;
    }

    // Mark only this launch as attempted. If iOS did not actually transition
    // permission state and still reports "default", keep this launch armed so
    // another explicit control tap can retry instead of silently losing the
    // permission opportunity. A genuine dismiss on other platforms remains a
    // one-attempt-per-launch flow.
    if (!(isIosInstalledPwa && permission === "default")) {
      permissionPromptAttemptedInSession[promptContext] = true;
    } else {
      fired = false;
      attach();
      return;
    }

    if (permission !== "granted") return;

    try {
      // Permission is already granted, so the remaining async setup can safely
      // fetch config, wait for the service worker and create/sync subscription.
      await enablePushNotifications({ forceConfigRefresh: false });
    } catch (error) {
      console.error("CheKhabare push subscription setup failed:", error);
    }
  };

  // Install immediately during startup. Capture phase lets this run before
  // React handlers that may navigate to a news URL or change app modes.
  attach();

  // Best-effort config warmup only; it never gates the permission gesture.
  loadServerConfig().catch(() => {});

  return true;
};
